/*!
 * @jscrypto/kdfs v0.9.1
 * Copyright 2026 Chen, Yi-Cyuan
 * Released under the MIT license
 */
import { concatBytes } from '@jscrypto/core';

function hmac(hash, key, input) {
    const normalizedKey = key.length > hash.blockSize ? hash.hash(key) : key;
    const innerKey = new Uint8Array(hash.blockSize);
    const outerKey = new Uint8Array(hash.blockSize);
    innerKey.set(normalizedKey);
    outerKey.set(normalizedKey);
    for (let i = 0; i < hash.blockSize; i++) {
        innerKey[i] ^= 0x36;
        outerKey[i] ^= 0x5c;
    }
    return hash.hash(concatBytes(outerKey, hash.hash(concatBytes(innerKey, input))));
}

const pbkdf2 = {
    kind: 'kdf',
    name: 'PBKDF2',
    derive(params, context) {
        const options = params;
        return derivePbkdf2({
            ...options,
            hash: context.getHash(options.hash ?? 'SHA256'),
        });
    },
};
const pbkdf2Preset = {
    kind: 'preset',
    name: 'pbkdf2',
    components() {
        return [pbkdf2];
    },
};
function derivePbkdf2(params) {
    assertPositiveInteger(params.iterations, 'PBKDF2 iterations');
    assertPositiveInteger(params.length, 'PBKDF2 length');
    if (params.input === undefined || params.input === null) {
        throw new TypeError('PBKDF2 requires input.');
    }
    if (params.salt === undefined || params.salt === null) {
        throw new TypeError('PBKDF2 requires salt.');
    }
    const password = toBytes(params.input);
    const salt = toBytes(params.salt);
    const blocks = [];
    const blockCount = Math.ceil(params.length / params.hash.digestSize);
    for (let index = 1; index <= blockCount; index++) {
        const counter = new Uint8Array([
            (index >>> 24) & 0xff,
            (index >>> 16) & 0xff,
            (index >>> 8) & 0xff,
            index & 0xff,
        ]);
        let block = hmac(params.hash, password, concatBytes(salt, counter));
        const result = block.slice();
        for (let iteration = 1; iteration < params.iterations; iteration++) {
            block = hmac(params.hash, password, block);
            for (let i = 0; i < result.length; i++) {
                result[i] ^= block[i];
            }
        }
        blocks.push(result);
    }
    return concatBytes(...blocks).slice(0, params.length);
}
function toBytes(input) {
    return typeof input === 'string' ? new TextEncoder().encode(input) : input;
}
function assertPositiveInteger(value, label) {
    if (!Number.isInteger(value) || value <= 0) {
        throw new RangeError(`${label} must be a positive integer.`);
    }
}

export { derivePbkdf2, pbkdf2, pbkdf2Preset };
//# sourceMappingURL=pbkdf2.mjs.map
